import { Navigate, useNavigate, useParams } from '@tanstack/react-router';
import {
  useCampaign,
  useInstagramConnect,
  useIsProviderConnected,
  useDisconnectSocialProvider,
} from '@/api';
import { ConnectSocialPage } from '@/components';
import { PageColumn } from '@/components/layout';
import { Button, Typography } from '@/primer/components';
import { findReservedUserProduct, hasClaimedProduct } from '@/utils';
import { hasActiveReservation, useReservationExpiry } from '@/hooks/useReservationTimer';
import { redirectToOAuth } from '@/utils/oauth';
import { SocialProvider } from '@/api/queries/settings/constants';
import { getApiErrorMessage } from '@/utils/apiError';
import { captureError } from '@/config/sentry';

/**
 * Connect-Instagram gate in the creator campaign flow. Reached from the tasks
 * hub's "Next" when Instagram isn't connected; the creator must connect before
 * continuing. Reuses ConnectSocialPage with campaign-flow navigation.
 */
export function CampaignConnectInstagram() {
  const { id } = useParams({ from: '/_authenticated/campaign/$id/connect' });
  const navigate = useNavigate();

  const {
    data: campaign,
    isLoading: isCampaignLoading,
    error: campaignError,
    isFetching: isCampaignFetching,
    refetch: refetchCampaign,
  } = useCampaign(id);
  const { isConnected, username, isLoading } = useIsProviderConnected(SocialProvider.INSTAGRAM);
  const {
    mutateAsync: connect,
    isPending: isConnecting,
    error: connectError,
  } = useInstagramConnect();
  const {
    mutateAsync: disconnect,
    isPending: isDisconnecting,
    error: disconnectError,
  } = useDisconnectSocialProvider();
  const reservedProduct = findReservedUserProduct(campaign?.userProducts);
  const reservationExpired = useReservationExpiry(reservedProduct?.reservedDate ?? '');

  const handleConnect = async () => {
    try {
      const { url } = await connect();
      redirectToOAuth(url, SocialProvider.INSTAGRAM);
    } catch (err) {
      if (err instanceof Error) captureError(err);
    }
  };

  const handleContinue = () => {
    if (!campaign) return;
    if (hasClaimedProduct(campaign.userProducts)) {
      navigate({ to: '/dashboard' });
      return;
    }
    if (!hasActiveReservation(findReservedUserProduct(campaign.userProducts))) {
      navigate({ to: '/campaign/$id/select-product', params: { id }, replace: true });
      return;
    }
    navigate({ to: '/campaign/$id/address', params: { id } });
  };

  if (campaignError || (!isCampaignLoading && !campaign)) {
    return (
      <PageColumn className="py-8 flex flex-col gap-4">
        <Typography variant="header" size="h1" as="h1" color="error">
          Unable to load campaign
        </Typography>
        <Button onClick={() => refetchCampaign()} isLoading={isCampaignFetching}>
          Retry
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate({ to: '/campaign/$id/tasks', params: { id } })}
        >
          Back to campaign
        </Button>
      </PageColumn>
    );
  }

  if (
    !isCampaignLoading &&
    campaign &&
    !hasClaimedProduct(campaign.userProducts) &&
    (reservationExpired || !hasActiveReservation(reservedProduct))
  ) {
    return <Navigate to="/campaign/$id/select-product" params={{ id }} replace />;
  }

  return (
    <ConnectSocialPage
      providerName="Instagram"
      icon="instagram"
      isConnected={isConnected}
      username={username}
      isLoadingConnection={isLoading || isCampaignLoading}
      isConnecting={isConnecting}
      isDisconnecting={isDisconnecting}
      error={
        connectError
          ? getApiErrorMessage(connectError, {
              fallback: "We couldn't connect Instagram right now. Please try again.",
            })
          : disconnectError
            ? getApiErrorMessage(disconnectError, {
                fallback: "We couldn't disconnect Instagram right now. Please try again.",
              })
            : ''
      }
      onConnect={handleConnect}
      onDisconnect={() => disconnect(SocialProvider.INSTAGRAM)}
      onBack={() => navigate({ to: '/campaign/$id/tasks', params: { id } })}
      backLabel={campaign?.name ?? 'Your program'}
      onContinue={handleContinue}
      continueLabel="Continue"
      requireConnection
    />
  );
}

export default CampaignConnectInstagram;
